
import { DateField } from "@/components/form/input/DateField";
import TextField from "@/components/form/input/TextField";
import { TimePickerField } from "@/components/form/input/TimePickerField";
import { FixedJobSearchField } from "@/components/form/search-fixed-job";
import { TeacherSearchField } from "@/components/form/search-teacher";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { FixedJob } from "@/hooks/useFixedJobSearch";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type Input = {
  teacherId: string;
  date: string;
  startTime: string;
  endTime: string;
  fixedJob: FixedJob;
  classType: string;
  subject: string;
  students: string;
  student_codes: string;
  educationLevel: string;
  branch: string;
  language: string;
}

export default function SpecialClassForm() {
  const { t } = useTranslation();
  const { control, setValue, handleSubmit, register, watch, reset } = useForm<Input>({
    defaultValues: {
      teacherId: "",
      date: "",
      startTime: "",
      endTime: "",
      classType: "",
      subject: "",
      students: "",
      student_codes: "",
      educationLevel: "",
      branch: "",
      language: ""
    }
  });

  const watchFixedJob = watch("fixedJob");

  const { profile } = useProfile();

  const onSubmit = async (data: Input) => {
    console.log(data);
    if (!data.date || !data.startTime || !data.endTime || !data.fixedJob.time_slot_id || !data.teacherId || !data.student_codes) {
      toast({
        title: "Error",
        description: t('specialClass.errorMissing'),
        variant: "destructive",
      });
      return;
    }

    try {
      // Create a unique job_id for this special class
      const currentDate = new Date().getTime();
      const specialJobId = currentDate; // Use timestamp as number
      
      // Split student codes and insert each one
      const studentCodes = data.student_codes.split(",").map((code) => code.trim());
      
      // Use time_slot_id (the actual UUID) instead of the numeric id
      const fixedJobIdInt = parseInt(data.fixedJob.time_slot_id.replace(/-/g, '').substring(0, 10), 16) || null;
      
      // Prepare the data for batch insert
      const jobStudents = studentCodes.map((code) => ({
        job_id: specialJobId,
        student_id: code,
        is_special_job: true,
        class_program: 'TEMPORARY',
        approve_status: "pending",
        status: "WAITING",
        fixed_job_id: fixedJobIdInt
      }));

      const { error: jobStudentError } = await supabase.from("job_students").insert(jobStudents);

      if (jobStudentError) {
        console.log(jobStudentError);
        toast({
          title: "Error",
          description: t('specialClass.errorCreate'),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: t('specialClass.successCreate'),
      });
      reset();
    } catch (error) {
      console.error("Error creating special class:", error);
      toast({
        title: "Error",
        description: t('specialClass.errorUnexpected'),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (profile) {
      setValue("teacherId", profile.teacher_id);
    }
  }, [profile, setValue]);

  const mapCourseType = (courseType: string) => {
    switch (courseType) {
      case "REGULAR":
        return "ปกติ";
      case "INTER":
        return "Inter";
      default:
        return "";
    }
  }

  useEffect(() => {
    if (watchFixedJob) {
      setValue("students", watchFixedJob.student_names);
      setValue("student_codes", watchFixedJob.student_codes);
      setValue("subject", watchFixedJob.subjects);
      setValue("educationLevel", watchFixedJob.education_level);
      setValue("branch", watchFixedJob.branch);
      setValue("language", watchFixedJob.language);
      setValue("classType", `${watchFixedJob.class_type} ${mapCourseType(watchFixedJob.course_type)}`);
    }
  }, [watchFixedJob, setValue]);

  return (
    <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
      <FixedJobSearchField control={control} setValue={setValue} name="fixedJob" targetField="fixedJob" teacherCode={profile?.teacher_id} />

      <TeacherSearchField disabled control={control} setValue={setValue} name="teacherId" targetField="teacherId" teacherCode={profile?.teacher_id} />

      <TextField readOnly name="students" field={{ id: "students", name: "students", type: "text", label: t('specialClass.students') }} register={register} />
      <TextField readOnly name="classType" field={{ id: "classType", name: "classType", type: "text", label: t('specialClass.classType') }} register={register} />
      <TextField readOnly name="subject" field={{ id: "subject", name: "subject", type: "text", label: t('specialClass.subject') }} register={register} />
      <TextField readOnly name="educationLevel" field={{ id: "educationLevel", name: "educationLevel", type: "text", label: t('specialClass.educationLevel') }} register={register} />
      <TextField readOnly name="branch" field={{ id: "branch", name: "branch", type: "text", label: t('specialClass.branch') }} register={register} />
      <TextField readOnly name="language" field={{ id: "language", name: "language", type: "text", label: t('specialClass.language') }} register={register} />

      <DateField control={control} setValue={setValue} name="date" field={{ id: "date", name: "date", type: "date", label: t('specialClass.date') }} />
      <div className="flex flex-col w-full gap-2">
        <TimePickerField control={control} setValue={setValue} name="startTime" field={{ id: "startTime", name: "startTime", type: "time", label: t('specialClass.startTime') }} />
        <TimePickerField control={control} setValue={setValue} name="endTime" field={{ id: "endTime", name: "endTime", type: "time", label: t('specialClass.endTime') }} />
      </div>
      <Button type="submit">{t('specialClass.submit')}</Button>
    </form>
  );
}
